import React, { useContext, useEffect, useRef, useState } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { HarvestingWrapper, StyledGroup } from '../GrassSilageStep/style';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox, Form } from 'semantic-ui-react';
import { StyledDivider } from '../ElevenStep/style';
import { FormFields } from '~/components/FormFields';
import { ClimaOptionsContext } from '.';
import { CORN_SILAGE_ID } from '../..';
import HarvestSlider from '../GrassSilageStep/HarvestSlider';
import { handleErrors } from '~/utils/helpers';
import CO2Service from '~/api/services/co2';
import { useParams } from 'react-router-dom';
import InfoPopup from '~/components/InfoPopup';
import LinkPopup from '../LinkPopup';
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon';

const FirstCornStep: React.FC = () => {
  const { id } = useParams()
  const { control, setValue, clearErrors } = useFormContext()
  const { grainContentTypes, maturityLevels, silageTypes, fieldSizes } = useContext(ClimaOptionsContext)
  const harvestingOptions = silageTypes?.find(silageType => silageType.id === CORN_SILAGE_ID)?.harvestingMethods || []
  const isFirstMaizeRender = useRef(true)

  const watchHarvesting = useWatch({ control, name: 'maizeSilage.harvestingMethods' }) || []
  const watchMaturity = useWatch({ control, name: 'maizeSilage.maturityLevelId' })
  const watchGrainContent = useWatch({ control, name: 'maizeSilage.grainContent' })
  const watchStroke = useWatch({ control, name: 'maizeSilage.averageFieldSize' })

  const watchHarvestingMethod = useWatch({ control, name: 'maizeSilage.harvestingMethods[0]' })
  const watchMaturityLevel = useWatch({ control, name: 'maizeSilage.maturityLevelId' })

  const isMaturity = Boolean(watchHarvestingMethod?.hasMaturityLevelOptions)
  const isGrain = Boolean(maturityLevels.find(maturityLevel => maturityLevel.id === watchMaturityLevel)?.hasGrainContentOptions)

  const [isLoadingPredefined, setIsLoadingPredefined] = useState(false)

  useEffect(() => {
    if (!isGrain) {
      setValue('maizeSilage.grainContent', null)
      clearErrors('maizeSilage.grainContent')
    }
  }, [isGrain])

  useEffect(() => {
    if (!isMaturity) {
      setValue('maizeSilage.maturityLevelId', null)
      clearErrors('maizeSilage.maturityLevelId')
    }
  }, [isMaturity])

  useEffect(() => {
    if (isFirstMaizeRender.current) {
      isFirstMaizeRender.current = false
      return
    }

    if (watchHarvestingMethod?.id) {
      fetchPredefinedData()
    }
  }, [watchHarvestingMethod?.id, watchMaturityLevel, watchGrainContent])

  async function fetchPredefinedData() {
    setIsLoadingPredefined(true)
    try {
      const payload = {
        harvestingMethodId: watchHarvestingMethod?.id,
        maturityLevelId: watchMaturityLevel || '',
        grainContent: watchGrainContent || ''
      }
      const response = await CO2Service.getMaizeSilagePredefinedData(id, payload)
      setValue('maizeSilage.freshMassYield', response.data.freshMassYield)
      setValue('maizeSilage.energy', response.data.energy)
      setValue('maizeSilage.rawProtein', response.data.rawProtein)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingPredefined(false)
    }

  }

  function handleChecked(harvestingOption) {
    if (watchHarvesting.length === 0 && !harvestingOption?.hasMaturityLevelOptions) {
      setValue('maizeSilage.grainContent', null)
      clearErrors('maizeSilage.grainContent')
    }
    return [...watchHarvesting, { ...harvestingOption, percent: watchHarvesting.length === 0 ? '100' : 0 }]
  }

  function handleUnchecked(harvestingOption, indexChecked) {
    //deselection of secundary harvest
    if (watchHarvesting?.length > 0 && indexChecked > 0) {
      const tempWatchHarvest = [...watchHarvesting]
      tempWatchHarvest[0].percent = 100
      return tempWatchHarvest.filter(harvest => harvest.id !== harvestingOption.id)
    }

    //deselection of primary harvest
    return []
  }

  return <SilageStepLayout
    infoText={`Die Abfrage zum Futterbauverfahren Silomais bezieht sich auf eine durchschnittliche Anbaufläche Silomais Ihres Betriebs. 
      Bitte berücksichtigen Sie dies bei der Eingabe Ihrer Daten. 
      Falls sich Bewirtschaftung oder Erträge auf mehreren Flächen unterscheiden, bilden Sie geeignete Mittelwerte, um eine repräsentative Angabe zu ermöglichen.`}
  >
    <StyledGroup widths={'equal'}>
      <Controller
        control={control}
        name='maizeSilage.harvestingMethods'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <HarvestingWrapper error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{'Ernteverfahren'}</div>
                <InfoPopup
                  content={'Das Ernteverfahren wirkt sich auf die Arbeitsgänge aus.'}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />
              </div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {harvestingOptions.map(harvestingOption => {

                const indexChecked = watchHarvesting.findIndex(harvesting => harvesting.id === harvestingOption.id)
                const harvestChecked = indexChecked >= 0
                const singleHarvestSelected = watchHarvesting.length === 1
                const maxHarvestSelected = watchHarvesting.length === 2
                const displayProcent = maxHarvestSelected && harvestChecked
                const displaySliderLabels = maxHarvestSelected && harvestChecked && watchHarvesting[1].id === harvestingOption.id

                return <Form.Field key={harvestingOption.id}>
                  <Checkbox
                    key={harvestingOption.id}
                    style={{ marginBottom: 3 }}
                    label={displayProcent ? `${harvestingOption.label} (${watchHarvesting[indexChecked]?.percent}%)` : harvestingOption.label}
                    className={singleHarvestSelected && !harvestChecked ? 'disable-harvest-method' : ''}
                    disabled={maxHarvestSelected && !harvestChecked}
                    checked={harvestChecked}
                    onChange={(_, data) => {
                      const harvestingOptions = data.checked ?
                        handleChecked(harvestingOption) :
                        handleUnchecked(harvestingOption, indexChecked)

                      setValue('maizeSilage.harvestingMethods', harvestingOptions)
                      if (watchHarvesting?.length > 0) {
                        clearErrors('maizeSilage.harvestingMethods')
                      }
                    }}
                  />
                  {displaySliderLabels && <HarvestSlider
                    primaryHarvestKey='maizeSilage.harvestingMethods[0].percent'
                    secondaryHarvestKey='maizeSilage.harvestingMethods[1].percent'
                  />}
                </Form.Field>
              })}
            </div>
          </HarvestingWrapper>
        )}
      />
      {isMaturity && <Controller
        control={control}
        name='maizeSilage.maturityLevelId'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Reifegrad'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {maturityLevels.map(maturityLevelOption => {
                return <Checkbox
                  radio
                  key={maturityLevelOption.id}
                  label={maturityLevelOption.label}
                  checked={watchMaturity === maturityLevelOption.id}
                  onClick={() => {
                    setValue('maizeSilage.maturityLevelId', maturityLevelOption.id)
                    clearErrors('maizeSilage.maturityLevelId')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />}
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      {isGrain && isMaturity && <Controller
        control={control}
        name='maizeSilage.grainContent'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Körneranteil'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {grainContentTypes.map(grainContentType => {
                return <Checkbox
                  radio
                  key={grainContentType.key}
                  label={grainContentType.label}
                  checked={watchGrainContent === grainContentType.key}
                  onClick={() => {
                    setValue('maizeSilage.grainContent', grainContentType.key)
                    clearErrors('maizeSilage.grainContent')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />}
      <Controller
        control={control}
        name='maizeSilage.averageFieldSize'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{'Durchschnittliche Schlaggröße'}</div>
                <InfoPopup
                  content={'Geben sie an, wie groß ihre Flächen im Durchschnitt sind. Die Wahl der durchschnittlichen Schlaggröße wirkt sich auf die Kalkulation des Dieselbedarfs aus.'}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />
                <LinkPopup
                  content={'Ackerschlagkartei'}
                />
              </div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {fieldSizes.map(avgFieldSize => {
                return <Checkbox
                  radio
                  key={avgFieldSize.key}
                  label={avgFieldSize.label}
                  checked={watchStroke === avgFieldSize.key}
                  onClick={() => {
                    setValue('maizeSilage.averageFieldSize', avgFieldSize.key)
                    clearErrors('maizeSilage.averageFieldSize')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        decimalScale={2}
        allowDecimals
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Durchschnittliche Entfernung von Hofstelle zu den Äckern'}</label>
          <InfoPopup
            content={'Diese Angabe wirkt sich auf den Dieselverbrauch aus.'}
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
        </div>}
        suffix=' km'
        placeholder={'in km'}
        name={'maizeSilage.averageDistance'}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
            if (transformValue > 100) {
              return 'Der Trockenmassegehalt darf maximal 100 % TM betragen.'
            }
            return true
          }
        }}
        allowDecimals
        decimalScale={2}
        withoutAsterisk
        noErrorMessage
        suffix=' % TM'
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Trockenmassegehalt der Maissilage'}</label>
          <ErrorIcon
            control={control}
            name={'maizeSilage.dryMatter'}
            />
        </div>}
        placeholder={'in % TM'}
        name={'maizeSilage.dryMatter'}
      />

      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 999.99) {
              return 'Der Frischmasseertrag Silomais darf maximal 999,99 dt FM/ ha betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        noErrorMessage
        loading={isLoadingPredefined}
        suffix=' dt FM/ ha'
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <label>{'Frischmasseertrag Silomais'}</label>
          <InfoPopup
            content='Geben Sie hier den Ertrag an, den Sie beim Silomais in Frischmasse (dt/ha) durchschnittlich auf dem Feld erzielt haben.'
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <ErrorIcon
            control={control}
            name={'maizeSilage.freshMassYield'}
          />
        </div>}
        placeholder={'in dt FM/ ha'}
        name={'maizeSilage.freshMassYield'}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 99.99) {
              return 'Der Energiegehalt der Silage darf maximal 99,99 MJ NEL/kg TM betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        noErrorMessage
        suffix=' MJ NEL/kg TM'
        allowDecimals
        decimalScale={2}
        loading={isLoadingPredefined}
        disabled={isLoadingPredefined}
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <label>{'Energiegehalt der Silage'}</label>
          <InfoPopup
            content='Hier ist der durchschnittliche Energiegehalt gemeint. Falls Analysen der verschiedenen Maissilagen vorhanden sind, bilden sie einen Mittelwert.'
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <LinkPopup
            content={'Analyseergebnisse der Silagen'}
          />
          <ErrorIcon
            control={control}
            name={'maizeSilage.energy'}
          />
        </div>}
        placeholder={'in MJ NEL/kg TM'}
        name={'maizeSilage.energy'}
      />
      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 999.99) {
              return 'Der Rohproteingehalt der Silage darf maximal 999,99 g XP/kg TM betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        suffix=' g XP/kg TM'
        loading={isLoadingPredefined}
        disabled={isLoadingPredefined}
        noErrorMessage
        allowDecimals
        decimalScale={2}
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <label>{'Rohproteingehalt der Silage'}</label>
          <InfoPopup
            content='Hier ist der durchschnittliche Rohproteingehalt gemeint. Falls Analysen der verschiedenen Maissilagen vorhanden sind, bilden sie einen Mittelwert.'
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <LinkPopup
            content={'Analyseergebnisse der Silagen'}
          />
          <ErrorIcon
            control={control}
            name={'maizeSilage.rawProtein'}
          />
        </div>}
        placeholder={'in g XP/kg TM'}
        name={'maizeSilage.rawProtein'}
      />
    </StyledGroup>

  </SilageStepLayout>
}
export default FirstCornStep